/**
 * React Hook: Generate Documentation
 *
 * Uses React Query's useMutation to call the documentation generation REST API.
 * Automatically reads org ID and user ID from the auth store.
 *
 * @example
 * ```tsx
 * const { generateDocs, isPending, data, error } = useGenerateDocumentation();
 *
 * // On button click / tab switch:
 * await generateDocs({
 *   file_id: selectedFile.id,
 *   class_name: selectedFile.fullName,
 *   metadata_type: selectedFile.metadataType,
 * });
 * ```
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { getOrgAbortSignal, isAbortError } from '@/lib/org-scope';
import { generateDocumentation, getDocumentation } from '@/services/documentation';
import type {
  GenerateDocumentationInput,
  GenerateDocumentationResponse,
} from '@/services/documentation';

// ============================================================================
// Types
// ============================================================================

/** Subset of GenerateDocumentationInput — org_id / user_id are auto-injected */
export type GenerateDocumentationArgs = Omit<GenerateDocumentationInput, 'org_id' | 'user_id'>;

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to generate AI documentation for a metadata component.
 *
 * Reads `selectedOrg.id` and `user.id` from authStore_v1 automatically.
 * Caches the result in React Query under the key
 * `['documentation', orgId, file_id]`.
 */
export function useGenerateDocumentation() {
  const queryClient = useQueryClient();

  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const userId = useAuthStoreV1((state) => state.user?.id);

  const mutation = useMutation<
    GenerateDocumentationResponse,
    Error,
    GenerateDocumentationArgs
  >({
    mutationFn: async (args: GenerateDocumentationArgs) => {
      const orgId = selectedOrg?.id;

      if (!orgId) {
        throw new Error('No organization selected');
      }

      const input = {
        ...args,
        org_id: orgId,
        user_id: userId ?? '',
        enrich: args.enrich ?? true,
      };

      // Read first. Opening a document the org has already generated must not depend on
      // the budget: the read is a storage lookup that spends nothing and passes the
      // proxy's budget gate, whereas `POST /docs/generate` is blocked outright for an
      // over-budget seat — which is why previously-generated docs became unreadable.
      // Only a genuine miss falls through to generation, and being blocked THERE is
      // correct: that call really would spend.
      // Both calls are for this org: leaving it cancels them, so a late answer can neither
      // reach the screen nor be cached (onSuccess) under an org the cache already purged.
      const signal = getOrgAbortSignal();
      const existing = await getDocumentation(input, orgId, userId ?? '', signal);
      if (existing) return existing;

      return generateDocumentation(input, orgId, userId ?? '', signal);
    },

    onSuccess: (data, variables) => {
      // Cache the result by org + file so re-selecting the same file
      // is instant without an additional network round-trip.
      queryClient.setQueryData(
        ['documentation', selectedOrg?.id, variables.file_id],
        data,
      );
    },

    onError: (error) => {
      if (isAbortError(error)) return; // the org changed; not a failure
      console.error('❌ Failed to generate documentation:', error);
    },
  });

  return {
    /** Trigger documentation generation (returns a Promise) */
    generateDocs: mutation.mutateAsync,
    /** Whether a request is in-flight */
    isPending: mutation.isPending,
    /** Generated documentation data */
    data: mutation.data ?? null,
    /** Error, if any */
    error: mutation.error,
    /** Whether generation succeeded */
    isSuccess: mutation.isSuccess,
    /** Reset mutation state (clears data + error) */
    reset: mutation.reset,
  };
}
